import {useChatManager} from '@github-ui/copilot-chat/CopilotChatManagerContext'
import type {CopilotSpace, CopilotSpaceId} from '@github-ui/copilot-chat-types'
import {customCopilotApiPath} from '@github-ui/copilot-chat-helpers/custom-copilots-helpers'
import {COPILOT_SPACES_QUERY_KEY, copilotSpacesQueryKey} from '@github-ui/copilot-shared-ui/spaces/fetch'
import {useMutation, useQueryClient} from '@github-ui/react-query'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'

import {resourceMetadataToRailsPayload} from '../utils/payload-mapper'

/**
 * The input type for the upsertCopilotSpace mutation.
 * We only allow a subset of fields to be updated from the client.
 */
export type Input = Partial<
  Pick<
    CopilotSpace,
    'name' | 'iconType' | 'iconColor' | 'description' | 'generalInstructions' | 'resources' | 'visibility' | 'baseRole'
  >
> & {ownerId?: number; ownerType?: string}

export const useUpsertCopilotSpace = (id?: CopilotSpaceId) => {
  const manager = useChatManager()
  const queryClient = useQueryClient()

  const {mutateAsync: upsertCopilotSpace, ...rest} = useMutation({
    onSuccess: async () => {
      // refetch the index of spaces in the background but still wait for the specific space to be invalidated
      void queryClient.invalidateQueries({queryKey: COPILOT_SPACES_QUERY_KEY})
      if (id) {
        await queryClient.invalidateQueries({queryKey: copilotSpacesQueryKey(id)})
      }
    },
    mutationFn: async (input: Input) => {
      const url = id ? customCopilotApiPath(id) : '/custom_copilots'
      const method = id ? 'PUT' : 'POST'

      const body = {
        custom_copilot: {
          owner_id: input.ownerId,
          owner_type: input.ownerType,
          name: input.name,
          icon_type: input.iconType,
          icon_color: input.iconColor,
          description: input.description,
          general_instructions: input.generalInstructions,
          resources_attributes: input.resources?.map(r => ({
            id: r.databaseId,
            copilot_chat_attachment_id: r.copilotChatAttachmentId,
            resource_type: r.type,
            // The _destroy param is a special param used by Rails' accepts_nested_attributes_for. If set to true
            // the record will be deleted.
            _destroy: r.markedForDestroy,
            metadata: resourceMetadataToRailsPayload(r),
          })),
          visibility: input.visibility,
          base_role: input.baseRole,
        },
      }

      const response = await verifiedFetchJSON(url, {
        method,
        body,
      })

      const data = await response.json()

      if (!response.ok) {
        throw data.errorMessages
      }

      // For now most call sites need to use the customCopilots state.
      // We'll continue to call this until we've removed them
      // we need to call manager to update the custom copilot in the chat
      manager?.dispatch({
        type: 'SET_CUSTOM_COPILOT',
        customCopilot: data,
      })

      return data as CopilotSpace
    },
  })

  return {
    upsertCopilotSpace,
    ...rest,
  }
}
